import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, Download, Eye, FileText, 
  Loader2, AlertCircle
} from "lucide-react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/badge";
import { 
  downloadInvoicePdf
} from "../../redux/Actions/InvoiceActions";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { useTranslation } from "react-i18next";

// Helper function to check if invoice should be displayed as negative
const isCreditNote = (invoice: any): boolean => {
  // Check if it's a credit note from Zoho metadata
  if (invoice.zohoMetadata?.type === "credit_note" || invoice.invoiceType === "credit_note") {
    return true;
  }
  
  // Check if invoice number includes "CN" (Credit Note)
  if (invoice.invoiceNumber && invoice.invoiceNumber.toUpperCase().includes("CN")) {
    return true;
  }
  
  // Check if Zoho invoice number includes "CN"
  if (invoice.zohoInvoiceNumber && invoice.zohoInvoiceNumber.toUpperCase().includes("CN")) {
    return true;
  }
  
  return false;
};

// Helper function to format invoice amount
const formatInvoiceAmount = (invoice: any): string => {
  const amount = invoice.amount || 0;
  const displayAmount = isCreditNote(invoice) ? -amount : amount;
  
  return `${getDynamicCurrencySymbol()}${displayAmount.toFixed(2)}`;
};

interface ZohoInvoiceViewerProps {
  invoice: any;
  isOpen: boolean;
  onClose: () => void;
}

const ZohoInvoiceViewer: React.FC<ZohoInvoiceViewerProps> = ({
  invoice,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [fullPageLoading, setFullPageLoading] = useState(false);

  useEffect(() => {
    if (isOpen && invoice?.zohoInvoiceId) {
      loadPdfUrl();
    }
  }, [isOpen, invoice]);

  const loadPdfUrl = async () => {
    setFullPageLoading(true);
    setLoading(true);
    setError("");
    
    try {
      // Download PDF blob from Zoho and create blob URL for iframe
      const blob = await downloadInvoicePdf(invoice.id);
      const blobUrl = window.URL.createObjectURL(blob);
      setPdfUrl(blobUrl);
    } catch (err) {
      console.error("Error loading PDF:", err);
      setError(t("zohoInvoiceViewer.failedToLoadPDF"));
    } finally {
      setLoading(false);
      setFullPageLoading(false);
    }
  };

  // Cleanup blob URL when component unmounts or invoice changes
  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith("blob:")) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleDownload = async () => {
    try {
      setFullPageLoading(true);
      setLoading(true);
      
      // Download PDF blob using action
      const blob = await downloadInvoicePdf(invoice.id);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert(t("zohoInvoiceViewer.failedToDownloadPDF"));
    } finally {
      setLoading(false);
      setFullPageLoading(false);
    }
  };

  if (!isOpen || !invoice) return null;

  return (
    <AnimatePresence>
      {/* Full Page Loader */}
      {fullPageLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-[60] flex items-center justify-center px-4"
        >
          <div className="bg-white rounded-xl p-6 sm:p-8 shadow-2xl max-w-sm w-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-blue-600" />
              <p className="text-base sm:text-lg font-medium text-gray-900 text-center">{t("zohoInvoiceViewer.loadingPDF")}</p>
              <p className="text-xs sm:text-sm text-gray-500 text-center">{t("zohoInvoiceViewer.loadingPDFWait")}</p>
            </div>
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-lg sm:rounded-xl w-full max-w-6xl max-h-[98vh] sm:max-h-[95vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-3 sm:p-4 md:p-6 border-b bg-gray-50">
            {/* Close button - top right */}
            <div className="flex justify-end mb-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-10 w-10 p-0 flex-shrink-0"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 truncate">
                    {invoice.invoiceNumber}
                  </h2>
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className={
                      invoice.status?.name === "Paid" ? "border-green-200 text-green-700 text-xs" :
                      invoice.status?.name === "Unpaid" ? "border-yellow-200 text-yellow-700 text-xs" :
                      "border-red-200 text-red-700 text-xs"
                    }>
                      {invoice.status?.name?.toUpperCase() || "UNKNOWN"}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-end flex-wrap">
                {/* Invoice amount badge */}
                <Badge variant="outline" className="text-sm font-semibold px-3 py-1">
                  {formatInvoiceAmount(invoice)}
                </Badge>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading && !pdfUrl ? (
              <div className="flex items-center justify-center h-full p-4">
                <div className="text-center">
                  <Loader2 className="h-7 w-7 sm:h-8 sm:w-8 animate-spin text-blue-600 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600">{t("zohoInvoiceViewer.loadingInvoice")}</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full p-4">
                <div className="text-center max-w-md">
                  <AlertCircle className="h-7 w-7 sm:h-8 sm:w-8 text-red-600 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600 mb-4">{error}</p>
                  <Button onClick={loadPdfUrl} variant="outline" size="sm">
                    {t("zohoInvoiceViewer.tryAgain")}
                  </Button>
                </div>
              </div>
            ) : pdfUrl ? (
              <div className="h-full overflow-auto bg-gray-100">
                {/* Show action buttons on all devices */}
                <div className="p-4 sm:p-6 md:p-8 h-full flex flex-col justify-center">
                  <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 max-w-md mx-auto w-full">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FileText className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                        {t("zohoInvoiceViewer.viewInvoice")}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 mb-2">
                        {t("zohoInvoiceViewer.chooseHowToView")}
                      </p>
                      <div className="text-xs sm:text-sm text-gray-500">
                        {t("zohoInvoiceViewer.invoice", { number: invoice.invoiceNumber })}
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <Button
                        onClick={() => window.open(pdfUrl, "_blank")}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 h-11 sm:h-12 text-sm sm:text-base"
                      >
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                        {t("zohoInvoiceViewer.openPDFNewTab")}
                      </Button>
                      
                      {/* <Button
                        onClick={handleViewInZoho}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2 h-11 sm:h-12 text-sm sm:text-base"
                      >
                        <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5" />
                        View in Zoho
                      </Button> */}
                      
                      <Button
                        onClick={handleDownload}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2 h-11 sm:h-12 text-sm sm:text-base"
                        disabled={loading}
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 sm:h-5 sm:w-5" />
                        )}
                        {t("zohoInvoiceViewer.downloadPDF")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full p-4">
                <div className="text-center">
                  <FileText className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400 mx-auto mb-3 sm:mb-4" />
                  <p className="text-sm sm:text-base text-gray-600">{t("zohoInvoiceViewer.noPDFAvailable")}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer Info */}
          <div className="p-3 sm:p-4 border-t bg-gray-50">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium">{t("zohoInvoiceViewer.invoiceId")}</span>
                <span className="text-gray-900 font-mono text-xs">{invoice.id.slice(0, 12)}...</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium">{t("zohoInvoiceViewer.amount")}</span>
                <span className={`font-semibold ${isCreditNote(invoice) ? "text-red-600" : "text-gray-900"}`}>
                  {formatInvoiceAmount(invoice)}
                </span>
                <span className="hidden sm:inline mx-1">•</span>
                <span className="font-medium">{t("zohoInvoiceViewer.date")}</span>
                <span className="text-gray-900">{new Date(invoice.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ZohoInvoiceViewer;
